<template>
	<div class="table-container">
		<table class="table text-color-content-primary" aria-label="Fundrise alternatives comparison table">
			<thead>
				<tr>
					<th
						v-for="column in columns"
						:key="column.key"
						:class="{ 'bg-color-greige': column.key == 'fundrise' }"
					>
						<BaseSvgIcon v-if="column.icon" :name="column.icon" width="auto" height="42" />

						<span v-if="column.label" :class="{ 'show-for-sr': column.header }">{{ column.label }} </span>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="row in rows" :key="row.category">
					<td
						v-for="column in columns"
						:key="column.key"
						vue:is="column.header ? 'th' : 'td'"
						:class="{ 'bg-color-greige': column.key == 'fundrise' }"
					>
						<span v-html="row[column.key]" /> </td
				></tr>
			</tbody>
		</table>
	</div>
</template>

<script setup lang="ts">
interface TableColumn {
	key: string;
	label?: string;
	icon?: string;
	header?: boolean;
}

const columns: Array<TableColumn> = [
	{ key: 'category', label: 'Category', header: true },
	{ key: 'privateMarkets', label: 'Private markets' },
	{ key: 'fundrise', icon: 'fundrise-logo-wordmark' },
	{ key: 'publicMarkets', label: 'Public markets' }
];

const rows: Array<Record<string, string>> = [
	{
		category: 'Regulation',
		privateMarkets: 'More limited',
		fundrise: 'SEC regulated',
		publicMarkets: 'High'
	},
	{
		category: 'Liquidity',
		privateMarkets: 'Little to none',
		fundrise: 'Quarterly liquidity',
		publicMarkets: 'Instant / Daily'
	},
	{
		category: 'Volatility',
		privateMarkets: 'Lower',
		fundrise: 'Low',
		publicMarkets: 'Higher'
	},
	{
		category: 'Returns',
		privateMarkets: 'Typically higher',
		fundrise: `Typically higher <span class="no-wrap">risk-adjusted</span>`,
		publicMarkets: 'Typically lower'
	},
	{
		category: 'Fees',
		privateMarkets: 'Higher',
		fundrise: 'Lower',
		publicMarkets: 'Lower'
	},
	{
		category: 'Access',
		privateMarkets: 'UHNW / Institutional',
		fundrise: 'Anyone',
		publicMarkets: 'Anyone'
	}
];
</script>

<style lang="scss" scoped>
@use '../../../styles/constants/colors.scss' as *;
@use '../../../styles/utilities/respond-to.scss' as *;

.table-container {
	@include respond-to(smo) {
		overflow: scroll;
		overscroll-behavior-x: contain;
		position: relative;
	}

	.table {
		thead th,
		tbody td {
			position: relative;
			text-align: center;
			padding: 0.875rem;
		}
		thead th {
			vertical-align: bottom;
			padding: 0.875rem 0.5rem;
		}
		tbody tr {
			&:first-of-type {
				th {
					border-top: none;
				}
				td {
					border-top: 1px solid token('content-tertiary');
					&:first-of-type::before,
					&:last-of-type::before {
						position: absolute;
						top: -2px;
						box-sizing: border-box;
						display: inline-block;
						content: '';
						width: 3px;
						height: 3px;
						border-style: solid;
						border-width: 1px 1px 0 0;
						border-color: token('content-tertiary');
					}

					&:first-of-type::before {
						transform: rotate(-135deg);
						left: 0;
					}
					&:last-of-type::before {
						right: 0;
						transform: rotate(45deg);
					}
				}
			}
			th {
				padding: 0.5rem 2rem 0.5rem 1rem;
				text-align: left;
				font-weight: bold;
			}
			&:last-of-type td,
			&:last-of-type th {
				padding-bottom: 1.5rem;
			}
		}
	}
}
</style>
